<div class="step-panel">

  <div class="pg-title">
    Credit Decision
    <span class="badge b-b" style="vertical-align:middle;margin-left:8px">Step 6</span>
  </div>
  <div class="pg-sub">
    Consolidated credit evaluation across bureau data, bank statement analysis, and income assessment.
  </div>

  <!-- ══ LOADING ══ -->
  <div *ngIf="isLoading" style="padding:40px;text-align:center">
    <div class="spinner-border text-primary" role="status"></div>
    <div style="margin-top:12px;color:var(--txt3);font-size:13px">Fetching credit data…</div>
  </div>

  <ng-container *ngIf="!isLoading">

    <!-- ══ NO DATA WARNING ══ -->
    <div *ngIf="!detailsLoaded" class="alert info" style="margin-bottom:16px">
      <span style="flex-shrink:0">ℹ</span>
      <span>No credit bureau data has been recorded for this application yet.
        Complete the Bureau + AA step (Step 5) to generate data.</span>
    </div>

    <!-- ══ DECISION BANNER ══ -->
    <div class="dc-card" [ngClass]="decisionColor" *ngIf="decisionFlag !== '—'">
      <div class="dc-badge">{{ decisionFlag }}</div>
      <div class="dc-sub">Credit evaluation outcome based on bureau + bank statement data</div>
    </div>

    <!-- ══ BUREAU SNAPSHOT ══ -->
    <div class="card">
      <div class="card-title">
        <div class="ct-bar" style="background:#6366f1;"></div>
        Bureau Snapshot — Equifax
      </div>

      <div class="rg4">
        <div class="rg bv">
          <div class="rg-l">Credit Score</div>
          <div class="rg-v">{{ creditScore }}</div>
          <div class="rg-s">{{ +creditScore >= 700 ? 'Good band' : +creditScore >= 600 ? 'Standard band' : 'Subprime' }}</div>
        </div>
        <div class="rg" [ngClass]="riskGrade === 'A' || riskGrade === 'B' ? 'gv' : riskGrade === 'C' ? 'av' : 'rv'">
          <div class="rg-l">Risk Grade</div>
          <div class="rg-v">{{ riskGrade }}</div>
          <div class="rg-s">Engine assessment</div>
        </div>
        <div class="rg bv">
          <div class="rg-l">Active Loans</div>
          <div class="rg-v">{{ activeLoans }}</div>
          <div class="rg-s">Open tradelines</div>
        </div>
        <div class="rg gv">
          <div class="rg-l">Closed Loans</div>
          <div class="rg-v">{{ closedLoans }}</div>
          <div class="rg-s">Settled accounts</div>
        </div>
      </div>

      <div class="rg4" style="margin-top:10px">
        <div class="rg" [ngClass]="worstDpd !== '—' && +worstDpd > 30 ? 'rv' : 'gv'">
          <div class="rg-l">Worst DPD</div>
          <div class="rg-v">{{ worstDpd }}</div>
          <div class="rg-s">Days past due</div>
        </div>
        <div class="rg av">
          <div class="rg-l">Enquiries (3M)</div>
          <div class="rg-v">{{ enquiries3m }}</div>
          <div class="rg-s">Last 3 months</div>
        </div>
        <div class="rg av">
          <div class="rg-l">Enquiries (6M)</div>
          <div class="rg-v">{{ enquiries6m }}</div>
          <div class="rg-s">Last 6 months</div>
        </div>
        <div class="rg" [ngClass]="writeOffFlag === '1' ? 'rv' : 'gv'">
          <div class="rg-l">Write-off</div>
          <div class="rg-v" [style.color]="writeOffFlag === '1' ? 'var(--rose)' : 'var(--green)'">
            {{ writeOffFlag === '1' ? 'Yes' : 'None' }}
          </div>
          <div class="rg-s">{{ writeOffFlag === '1' ? '⚠ Flagged' : '✓ Clean' }}</div>
        </div>
      </div>
    </div>

    <!-- ══ INCOME & REPAYMENT CAPACITY ══ -->
    <div class="card">
      <div class="card-title">
        <div class="ct-bar" style="background:#10b981;"></div>
        Income & Repayment Capacity
        <span class="ftag ft-auto" style="margin-left:4px">Bank Statement AI</span>
      </div>

      <div class="rg4">
        <div class="rg gv">
          <div class="rg-l">Avg Monthly Inflow</div>
          <div class="rg-v">{{ getBSValue('BS Avg Monthly Credit','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">Statement period avg</div>
        </div>
        <div class="rg gv">
          <div class="rg-l">Avg Monthly Salary</div>
          <div class="rg-v">{{ getBSValue('BS Avg Monthly Salary','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">
            <span [style.color]="getBSValue('BS Salary Detected','0') === '1' ? 'var(--green)' : 'var(--amber)'">
              {{ getBSValue('BS Salary Detected','0') === '1' ? '✓ Salary detected' : '⚠ Not detected' }}
            </span>
          </div>
        </div>
        <div class="rg bv">
          <div class="rg-l">Net Monthly Income</div>
          <div class="rg-v">{{ getBSValue('BS Net Monthly Income','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">FOIR base</div>
        </div>
        <div class="rg bv">
          <div class="rg-l">Income Trust Score</div>
          <div class="rg-v">{{ getBSValue('BS Income Trust Score','0') }}/100</div>
          <div class="rg-s">Consistency</div>
        </div>
      </div>

      <div class="rg4" style="margin-top:10px">
        <div class="rg" [style.background]="getBSValue('BS Income Drop 30Pct','0') === '1' ? 'rgba(225,29,72,0.06)' : ''">
          <div class="rg-l">Income Drop &gt; 30%</div>
          <div class="rg-v" [style.color]="getBSValue('BS Income Drop 30Pct','0') === '1' ? 'var(--rose)' : 'var(--green)'">
            {{ getBSValue('BS Income Drop 30Pct','0') === '1' ? 'Yes' : 'No' }}
          </div>
          <div class="rg-s">Volatility: {{ getBSValue('BS Income Volatility Pct','0') | number:'1.1-1' }}%</div>
        </div>
        <div class="rg" [style.background]="getBSValue('BS Salary Stopped Recently','0') === '1' ? 'rgba(225,29,72,0.06)' : ''">
          <div class="rg-l">Salary Stopped</div>
          <div class="rg-v" [style.color]="getBSValue('BS Salary Stopped Recently','0') === '1' ? 'var(--rose)' : 'var(--green)'">
            {{ getBSValue('BS Salary Stopped Recently','0') === '1' ? 'Yes' : 'No' }}
          </div>
          <div class="rg-s">Recently stopped</div>
        </div>
        <div class="rg" [style.background]="+getBSValue('BS Monthly Surplus','0') >= 0 ? 'rgba(5,150,105,0.06)' : 'rgba(225,29,72,0.06)'">
          <div class="rg-l">Monthly Surplus</div>
          <div class="rg-v" [style.color]="+getBSValue('BS Monthly Surplus','0') >= 0 ? 'var(--green)' : 'var(--rose)'">
            {{ getBSValue('BS Monthly Surplus','0') | currency:'INR':'symbol':'1.0-0' }}
          </div>
          <div class="rg-s">Income/Expense: {{ getBSValue('BS Income Expense Ratio','0') | number:'1.2-2' }}</div>
        </div>
        <div class="rg bv">
          <div class="rg-l">Repayment Capacity</div>
          <div class="rg-v">{{ getBSValue('BS Repayment Capacity Score','0') }}/100</div>
          <div class="rg-s">Savings rate: {{ getBSValue('BS Savings Rate Pct','0') | number:'1.1-1' }}%</div>
        </div>
      </div>
    </div>

    <!-- ══ FOIR & EMI LIABILITY ══ -->
    <div class="card">
      <div class="card-title">
        <div class="ct-bar" style="background:#6366f1;"></div>
        FOIR & EMI Liability
        <span class="ftag ft-auto" style="margin-left:4px">Bank Statement AI</span>
      </div>

      <div class="rg4">
        <div class="rg" [style.background]="+getBSValue('BS FOIR Percentage','0') > 50 ? 'rgba(225,29,72,0.06)' : 'rgba(5,150,105,0.06)'">
          <div class="rg-l">FOIR %</div>
          <div class="rg-v" [style.color]="+getBSValue('BS FOIR Percentage','0') > 50 ? 'var(--rose)' : 'var(--green)'">
            {{ getBSValue('BS FOIR Percentage','0') | number:'1.1-1' }}%
          </div>
          <div class="rg-s">Risk: {{ getBSValue('BS FOIR Risk Level','—') }}</div>
        </div>
        <div class="rg av">
          <div class="rg-l">Total Monthly Obligation</div>
          <div class="rg-v">{{ getBSValue('BS Total Monthly Obligation','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">EMI + recurring debits</div>
        </div>
        <div class="rg" [style.background]="+getBSValue('BS Existing EMI Count','0') > 0 ? 'rgba(217,119,6,0.06)' : ''">
          <div class="rg-l">Existing EMIs</div>
          <div class="rg-v">{{ getBSValue('BS Monthly EMI Amount','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">{{ getBSValue('BS Existing EMI Count','0') }} active loans detected</div>
        </div>
        <div class="rg" [style.background]="getBSValue('BS Hidden Liability Suspected','0') === '1' ? 'rgba(225,29,72,0.06)' : ''">
          <div class="rg-l">Hidden Liability</div>
          <div class="rg-v" [style.color]="getBSValue('BS Hidden Liability Suspected','0') === '1' ? 'var(--rose)' : 'var(--green)'">
            {{ getBSValue('BS Hidden Liability Suspected','0') === '1' ? 'Suspected' : 'None' }}
          </div>
          <div class="rg-s">Stress: {{ getBSValue('BS Liability Stress Score','0') }}/100</div>
        </div>
      </div>

      <div class="rg4" style="margin-top:10px">
        <div class="rg" [style.background]="getBSValue('BS EMI Bounce Count','0') !== '0' ? 'rgba(225,29,72,0.06)' : ''">
          <div class="rg-l">EMI Bounces</div>
          <div class="rg-v" [style.color]="getBSValue('BS EMI Bounce Count','0') !== '0' ? 'var(--rose)' : 'var(--green)'">
            {{ getBSValue('BS EMI Bounce Count','0') }}
          </div>
          <div class="rg-s">
            Cheque: {{ getBSValue('BS Cheque Bounce Count','0') }} ·
            NACH: {{ getBSValue('BS NACH Return Count','0') }}
          </div>
        </div>
        <div class="rg av">
          <div class="rg-l">Avg Monthly Outflow</div>
          <div class="rg-v">{{ getBSValue('BS Avg Monthly Debit','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">Total: {{ getBSValue('BS Total Debit','0') | currency:'INR':'symbol':'1.0-0' }}</div>
        </div>
        <div class="rg">
          <div class="rg-l">Expense Stability</div>
          <div class="rg-v">{{ getBSValue('BS Expense Stability Score','0') }}/100</div>
          <div class="rg-s">Volatility: {{ getBSValue('BS Expense Volatility Pct','0') | number:'1.1-1' }}%</div>
        </div>
        <div class="rg">
          <div class="rg-l">Cashflow Stability</div>
          <div class="rg-v">{{ getBSValue('BS Cashflow Stability Score','0') }}</div>
          <div class="rg-s">Score</div>
        </div>
      </div>
    </div>

    <!-- ══ BALANCE HEALTH ══ -->
    <div class="card">
      <div class="card-title">
        <div class="ct-bar" style="background:#0284c7;"></div>
        Balance Health
        <span class="ftag ft-auto" style="margin-left:4px">Bank Statement AI</span>
      </div>

      <div class="rg4">
        <div class="rg bv">
          <div class="rg-l">Avg Monthly Balance</div>
          <div class="rg-v">{{ getBSValue('BS Avg Monthly Balance','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">Median: {{ getBSValue('BS Median Monthly Balance','0') | currency:'INR':'symbol':'1.0-0' }}</div>
        </div>
        <div class="rg gv">
          <div class="rg-l">Max Balance</div>
          <div class="rg-v">{{ getBSValue('BS Maximum Balance','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">Peak in period</div>
        </div>
        <div class="rg" [style.background]="+getBSValue('BS Minimum Balance','0') < 5000 ? 'rgba(225,29,72,0.06)' : ''">
          <div class="rg-l">Min Balance</div>
          <div class="rg-v">{{ getBSValue('BS Minimum Balance','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">Low-balance days: {{ getBSValue('BS Low Balance Days','0') }}</div>
        </div>
        <div class="rg" [style.background]="+getBSValue('BS Negative Balance Days','0') > 0 ? 'rgba(225,29,72,0.06)' : 'rgba(5,150,105,0.06)'">
          <div class="rg-l">Negative Days</div>
          <div class="rg-v" [style.color]="+getBSValue('BS Negative Balance Days','0') > 0 ? 'var(--rose)' : 'var(--green)'">
            {{ getBSValue('BS Negative Balance Days','0') }}
          </div>
          <div class="rg-s">Penalty count: {{ getBSValue('BS Min Balance Penalty Count','0') }}</div>
        </div>
      </div>
    </div>

    <!-- ══ FRAUD & AUTHENTICITY ══ -->
    <div class="card"
      [style.border-left]="getBSValue('BS Tampering Flagged','0') === '1' || +getBSValue('BS Fraud Risk Score','0') > 50
        ? '4px solid var(--rose)'
        : '4px solid var(--green)'">
      <div class="card-title">
        <div class="ct-bar"
          [style.background]="getBSValue('BS Tampering Flagged','0') === '1' || +getBSValue('BS Fraud Risk Score','0') > 50
            ? 'var(--rose)' : 'var(--green)'"></div>
        Document Authenticity & Fraud Signals
        <span class="ftag ft-auto" style="margin-left:4px">Bank Statement AI</span>
        <span *ngIf="getBSValue('BS Tampering Flagged','0') === '1'"
          style="margin-left:8px;background:#fee2e2;color:#b91c1c;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:700;">
          🚨 TAMPERING FLAGGED
        </span>
        <span *ngIf="getBSValue('BS Tampering Flagged','0') !== '1'"
          style="margin-left:8px;background:#d1fae5;color:#065f46;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:700;">
          ✓ Authentic
        </span>
      </div>

      <div class="rg4">
        <div class="rg" [style.background]="getBSValue('BS Tampering Flagged','0') === '1' ? 'rgba(225,29,72,0.06)' : 'rgba(5,150,105,0.06)'">
          <div class="rg-l">File Auth Score</div>
          <div class="rg-v" [style.color]="getBSValue('BS Tampering Flagged','0') === '1' ? 'var(--rose)' : 'var(--green)'">
            {{ getBSValue('BS File Auth Score','0') }}/100
          </div>
          <div class="rg-s">Txn Auth: {{ getBSValue('BS Txn Auth Score','0') }}/100</div>
        </div>
        <div class="rg" [style.background]="+getBSValue('BS Fraud Risk Score','0') > 50 ? 'rgba(225,29,72,0.06)' : 'rgba(5,150,105,0.06)'">
          <div class="rg-l">Fraud Risk Score</div>
          <div class="rg-v" [style.color]="+getBSValue('BS Fraud Risk Score','0') > 50 ? 'var(--rose)' : 'var(--green)'">
            {{ getBSValue('BS Fraud Risk Score','0') }}/100
          </div>
          <div class="rg-s">{{ +getBSValue('BS Fraud Risk Score','0') > 50 ? 'High risk' : 'Low risk' }}</div>
        </div>
        <div class="rg" [style.background]="getBSValue('BS Balance Continuity Fails','0') !== '0' ? 'rgba(217,119,6,0.06)' : ''">
          <div class="rg-l">Balance Continuity Fails</div>
          <div class="rg-v" [style.color]="getBSValue('BS Balance Continuity Fails','0') !== '0' ? 'var(--amber)' : 'var(--green)'">
            {{ getBSValue('BS Balance Continuity Fails','0') }}
          </div>
          <div class="rg-s">Duplicate txns: {{ getBSValue('BS Duplicate Txn Count','0') }}</div>
        </div>
        <div class="rg" [style.background]="getBSValue('BS Missing Pages','0') === '1' ? 'rgba(217,119,6,0.06)' : ''">
          <div class="rg-l">Statement Integrity</div>
          <div class="rg-v" [style.color]="getBSValue('BS Missing Pages','0') === '1' ? 'var(--amber)' : 'var(--green)'">
            {{ getBSValue('BS Missing Pages','0') === '1' ? 'Incomplete' : 'Complete' }}
          </div>
          <div class="rg-s">Pages: {{ getBSValue('BS Page Count','—') }}</div>
        </div>
      </div>

      <!-- Fraud signal tags -->
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">
        <span *ngIf="getBSValue('BS Suspicious Dummy Name','0') === '1'"
          style="background:#fff7ed;color:#c2410c;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;border:1px solid #fed7aa;">
          ⚠️ Suspicious Dummy Name Transfer
        </span>
        <span *ngIf="getBSValue('BS Circular Txn Detected','0') === '1'"
          style="background:#fee2e2;color:#991b1b;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;border:1px solid #fecaca;">
          🚨 Circular Transaction Detected
        </span>
        <span *ngIf="getBSValue('BS UPI Circular Suspected','0') === '1'"
          style="background:#fee2e2;color:#991b1b;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;border:1px solid #fecaca;">
          🔄 UPI Circular Flow Suspected
        </span>
        <span *ngIf="+getBSValue('BS Gambling Txn Count','0') > 0"
          style="background:#fee2e2;color:#991b1b;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;border:1px solid #fecaca;">
          🎰 Gambling: {{ getBSValue('BS Gambling Txn Count','0') }} txn(s)
        </span>
        <span *ngIf="+getBSValue('BS Crypto Txn Count','0') > 0"
          style="background:#fef3c7;color:#92400e;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;border:1px solid #fde68a;">
          ₿ Crypto: {{ getBSValue('BS Crypto Txn Count','0') }} txn(s)
        </span>
        <span *ngIf="getBSValue('BS Artificial Balance Buildup','0') === '1'"
          style="background:#fee2e2;color:#991b1b;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;border:1px solid #fecaca;">
          📈 Artificial Balance Build-up Suspected
        </span>
        <span *ngIf="+getBSValue('BS Frequent Loan App Txns','0') > 0"
          style="background:#fef3c7;color:#92400e;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;border:1px solid #fde68a;">
          📱 Loan App Txns: {{ getBSValue('BS Frequent Loan App Txns','0') }}
        </span>
        <span *ngIf="!hasAnyFraudSignal()"
          style="background:#d1fae5;color:#065f46;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;border:1px solid #a7f3d0;">
          ✓ No fraud signals detected
        </span>
      </div>
    </div>

    <!-- ══ COMBINED RISK SCORECARD ══ -->
    <div class="card">
      <div class="card-title">
        <div class="ct-bar" style="background:#6366f1;"></div>
        Combined Risk Scorecard
      </div>

      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;">

        <div class="rg" style="flex-direction:column;gap:3px;"
          [style.background]="getScoreBg(+getBSValue('BS Income Trust Score','0'), 60)">
          <div class="rg-l">Income Trust</div>
          <div class="rg-v" [style.color]="getScoreColor(+getBSValue('BS Income Trust Score','0'), 60)">
            {{ getBSValue('BS Income Trust Score','0') }}/100
          </div>
        </div>

        <div class="rg" style="flex-direction:column;gap:3px;"
          [style.background]="getScoreBg(+getBSValue('BS Expense Stability Score','0'), 40)">
          <div class="rg-l">Expense Stability</div>
          <div class="rg-v" [style.color]="getScoreColor(+getBSValue('BS Expense Stability Score','0'), 40)">
            {{ getBSValue('BS Expense Stability Score','0') }}/100
          </div>
        </div>

        <div class="rg" style="flex-direction:column;gap:3px;"
          [style.background]="getScoreBg(100 - +getBSValue('BS Liability Stress Score','0'), 50)">
          <div class="rg-l">Liability Stress</div>
          <div class="rg-v" [style.color]="getScoreColor(100 - +getBSValue('BS Liability Stress Score','0'), 50)">
            {{ getBSValue('BS Liability Stress Score','0') }}/100
          </div>
        </div>

        <div class="rg" style="flex-direction:column;gap:3px;"
          [style.background]="getScoreBg(100 - +getBSValue('BS Fraud Risk Score','0'), 50)">
          <div class="rg-l">Fraud Risk</div>
          <div class="rg-v" [style.color]="getScoreColor(100 - +getBSValue('BS Fraud Risk Score','0'), 50)">
            {{ getBSValue('BS Fraud Risk Score','0') }}/100
          </div>
        </div>

        <div class="rg" style="flex-direction:column;gap:3px;"
          [style.background]="getScoreBg(+getBSValue('BS Txn Auth Score','0'), 60)">
          <div class="rg-l">Txn Auth</div>
          <div class="rg-v" [style.color]="getScoreColor(+getBSValue('BS Txn Auth Score','0'), 60)">
            {{ getBSValue('BS Txn Auth Score','0') }}/100
          </div>
        </div>

        <div class="rg" style="flex-direction:column;gap:3px;"
          [style.background]="getScoreBg(+getBSValue('BS Repayment Capacity Score','0'), 50)">
          <div class="rg-l">Repayment Capacity</div>
          <div class="rg-v" [style.color]="getScoreColor(+getBSValue('BS Repayment Capacity Score','0'), 50)">
            {{ getBSValue('BS Repayment Capacity Score','0') }}/100
          </div>
        </div>

        <div class="rg" style="flex-direction:column;gap:3px;"
          [style.background]="getScoreBg(+getBSValue('BS File Auth Score','0'), 80)">
          <div class="rg-l">File Auth</div>
          <div class="rg-v" [style.color]="getScoreColor(+getBSValue('BS File Auth Score','0'), 80)">
            {{ getBSValue('BS File Auth Score','0') }}/100
          </div>
        </div>

        <div class="rg" style="flex-direction:column;gap:3px;"
          [style.background]="getBSValue('BS Cash Heavy Profile','0') === '1' ? 'rgba(217,119,6,0.06)' : 'rgba(5,150,105,0.06)'">
          <div class="rg-l">Cash Heavy</div>
          <div class="rg-v" [style.color]="getBSValue('BS Cash Heavy Profile','0') === '1' ? 'var(--amber)' : 'var(--green)'">
            {{ getBSValue('BS Cash Heavy Profile','0') === '1' ? 'Yes' : 'No' }}
          </div>
        </div>

      </div>
    </div>

    <!-- ══ CASH & UPI FLOW ══ -->
    <div class="card">
      <div class="card-title">
        <div class="ct-bar" style="background:#8b5cf6;"></div>
        Cash & UPI Flow
        <span class="ftag ft-auto" style="margin-left:4px">Bank Statement AI</span>
      </div>

      <div class="rg4">
        <div class="rg gv">
          <div class="rg-l">UPI Inflow</div>
          <div class="rg-v">{{ getBSValue('BS UPI Inflow Total','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">{{ getBSValue('BS UPI Inflow Count','0') }} txns</div>
        </div>
        <div class="rg av">
          <div class="rg-l">UPI Outflow</div>
          <div class="rg-v">{{ getBSValue('BS UPI Outflow Total','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">{{ getBSValue('BS UPI Outflow Count','0') }} txns</div>
        </div>
        <div class="rg av">
          <div class="rg-l">ATM Withdrawals</div>
          <div class="rg-v">{{ getBSValue('BS ATM Withdrawal Amount','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">{{ getBSValue('BS Cash Withdrawal Count','0') }} txns · {{ getBSValue('BS Cash Withdrawal Ratio Pct','0') | number:'1.1-1' }}% of debit</div>
        </div>
        <div class="rg">
          <div class="rg-l">Paytm Top-ups</div>
          <div class="rg-v">{{ getBSValue('BS Paytm Topup Amount','0') | currency:'INR':'symbol':'1.0-0' }}</div>
          <div class="rg-s">{{ getBSValue('BS Paytm Topup Count','0') }} txns</div>
        </div>
      </div>
    </div>

    <!-- ══ NAVIGATION ══ -->
    <div class="nav-bar">
      <button class="btn btn-sec" (click)="prev.emit()">← Back</button>
      <button class="btn btn-pri btn-lg" (click)="proceed()">Continue →</button>
    </div>

  </ng-container>

</div>